import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import _ from 'lodash';
import {
  Badge,
  Container,
  Typography,
  Grid,
  Row,
  Col,
  Button,
  Menu,
  MenuItem,
  Option,
  Select,
  Switch,
  Input,
  Toast,
  ToastTitle,
} from '@sm/wds-react';
import { IconStarFilled, IconMore, IconArrowLeft, IconArrowRight } from '@sm/wds-icons';
import MultipleChoice from '../../components/MultipleChoice';
import { mapQuestionTypeFromAPI, mapAnswersFromAPI } from './QuestionToEditFormatter';
import mapQuestionToAPI from './QuestionToSaveFormatter';
import { MC_QUESTIONS, sendMessage } from './helper';

import { CreateWizardConfigContext } from '../../CreateWizardConfigContext';

import './new-survey.scss';

const NewSurvey = ({ match }) => {
  const { createWizardConfig, setCreateWizardConfig } = useContext(CreateWizardConfigContext);
  const {
    primaryCtaCopy,
    style,
    survey,
    redirectToEditorPage,
    redirectTo,
    readOnlySurvey,
    showPrimaryCtaIcon,
    parentDomain,
  } = createWizardConfig;
  const surveyTypeSelected = redirectToEditorPage ? redirectTo : match.params.surveyTypeSelected;
  const pollDefaultConfig = {
    survey,
    maxQuestions: 1,
    maxQuestionsBasic: 10,
    questionConfig: [
      {
        forTypes: ['single_choice', 'dropdown'],
        maxOptions: 4,
        customConfigs: [
          {
            id: 'comments',
            label: 'Allow comments',
          },
        ],
        hideRequired: true,
      },
    ],
    types: ['poll'],
    selectedType: surveyTypeSelected,
    showBadge: false,
    sendPayloadOnAllUpdates: false,
    hideCreateSurveyButton: false,
    hideCancelSurveyButton: true,
    style,
  };
  const surveyDefaultConfig = {
    survey,
    maxQuestions: 20,
    maxQuestionsBasic: 10,
    questionConfig: [
      {
        forTypes: ['single_choice', 'dropdown', 'multiple_choice'],
        maxOptions: 10,
        customConfigs: [
          {
            id: 'comments',
            label: 'Allow comments',
          },
        ],
        hideRequired: true,
      },
      {
        forTypes: ['single_textbox', 'comment_box', 'star_rating', 'slider'],
        customConfigs: [{}],
        hideRequired: true,
      },
    ],
    types: ['Survey', 'Quiz'],
    selectedType: surveyTypeSelected,
    showBadge: false,
    sendPayloadOnAllUpdates: false,
    hideCreateSurveyButton: false,
    hideCancelSurveyButton: true,
    style,
  };
  const defaultInputConfig = surveyTypeSelected === 'poll' ? pollDefaultConfig : surveyDefaultConfig;

  const {
    hideCreateSurveyButton,
    hideCancelSurveyButton,
    sendPayloadOnAllUpdates,
    questionConfig,
    maxQuestions,
    types,
  } = defaultInputConfig;

  const NEW_QUESTION = {
    id: new Date().getUTCMilliseconds().toString(),
    question: '',
    questionType: 'single_choice',
    answers: [
      { answer: '', score: '' },
      { answer: '', score: '' },
      { answer: '', score: '' },
    ],
    min: 0,
    max: 100,
    required: false,
    isNew: true,
    isUpdated: false,
  };

  const [inputData] = useState(defaultInputConfig);

  const [surveyDetails, setSurveyDetails] = useState(survey);

  const [questions, setQuestions] = useState([_.clone(NEW_QUESTION)]);

  const [surveyTitle, setSurveyTitle] = useState(survey && survey.title);

  const [surveyType, setSurveyType] = useState(surveyTypeSelected);

  const [shouldRedirectBackToHome, setShouldRedirectBackToHome] = useState(false);

  const [redirectUrl, setRedirectUrl] = useState('/createwizard/');

  const isNewSurvey = Object.keys(survey).length === 0;

  const edit = redirectToEditorPage && !isNewSurvey;

  const loadMappedQuestionsList = useCallback(
    surveyData => {
      const questionsList = [];
      if (edit && surveyData) {
        surveyData.pages.forEach(page => {
          page.questions.forEach(question => {
            const q = {
              id: question.id || question.question_id,
              question: question.headings[0].heading,
              questionType: mapQuestionTypeFromAPI(question.family, question.subtype, question.display_options),
              answers: mapAnswersFromAPI(question),
              min: question.validation ? question.validation.min : 0,
              max: question.validation ? question.validation.max : 100,
              allowComments: false,
              required: !!question.required,
              isNew: surveyDetails && surveyDetails.id === undefined,
              isUpdated: false,
              initialPosition: question.position,
            };
            if (q) {
              questionsList.push(q);
            }
          });
        });
        setQuestions(questionsList);
      }
    },
    [edit, surveyDetails]
  );

  useEffect(() => {
    loadMappedQuestionsList(survey);
    setSurveyDetails(survey);
  }, [edit, loadMappedQuestionsList, survey]);

  let questionArray = [];
  let maxAnsChoices;

  questionConfig.forEach(({ forTypes, maxOptions }) => {
    const isMCQuestions = forTypes.every(val => MC_QUESTIONS.includes(val));
    if (isMCQuestions) {
      maxAnsChoices = maxOptions;
    }
    questionArray.push(forTypes);
  });

  questionArray = [].concat(...questionArray);

  const MAX_QUESTIONS = maxQuestions;

  const customStylings = style;

  const root = document.documentElement;

  root.style.setProperty('--customButtonColor', customStylings.buttonColor);
  root.style.setProperty('--customTextColor', customStylings.buttonTextColor);
  root.style.setProperty('--customSecondaryButtonColor', customStylings.secondaryButtonColor);
  root.style.setProperty('--customSecondaryTextColor', customStylings.secondaryButtonTextColor);
  root.style.setProperty('--customBadgeColor', customStylings.badgeColor);
  root.style.setProperty('--customBadgeTextColor', customStylings.badgeTextColor);

  const showMore = false;

  const hasresponses = false;
  const [showToast, setShowToast] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [setDeletePromptQuestion] = useState(undefined);
  const [questionsToDelete, setQuestionsToDelete] = useState([]);

  const QUESTION_TYPES_SURVEY = [
    { id: 1, value: 'single_choice', text: 'Multiple choice' },
    { id: 2, value: 'dropdown', text: 'Dropdown' },
    { id: 3, value: 'multiple_choice', text: 'Checkboxes' },
    { id: 4, value: 'single_textbox', text: 'Single textbox' },
    { id: 5, value: 'comment_box', text: 'Comment box' },
    { id: 6, value: 'star_rating', text: 'Star rating' },
    { id: 7, value: 'slider', text: 'Slider' },
  ];

  const getQuestionTypes = () => {
    return QUESTION_TYPES_SURVEY.filter(i => questionArray.includes(i.value));
  };

  const hasErrors = () => {
    if (surveyTitle === '' && surveyType !== 'poll') {
      return { focus: 'surveyTitle', message: 'Please enter survey title' };
    }

    for (let x = 0; x < questions.length; x += 1) {
      const question = questions[x];

      if (question.question === '') {
        return {
          focus: `question-${question.id}`,
          message: 'Please enter question text',
        };
      }

      if (MC_QUESTIONS.includes(question.questionType)) {
        let answerCount = 0;
        let answerId = 0;
        let hasScore = false;
        for (let y = 0; y < question.answers.length; y += 1) {
          const answer = question.answers[y];
          if (answer.answer !== '') {
            answerCount += 1;
          } else if (answerId === 0) {
            answerId = y + 1;
          }
          if (surveyType === 'quiz') {
            if (!hasScore && answer.score > 0) {
              hasScore = true;
            }
          }
        }
        if (answerCount < 2) {
          return {
            focus: `question-${question.id}-answer${answerId}`,
            message: 'Please enter at least two choices',
          };
        }
        if (surveyType === 'quiz' && !hasScore) {
          return {
            focus: `question-${question.id}-score1`,
            message: 'Please enter at least one score for your answers',
          };
        }
      }

      if (question.questionType === 'slider') {
        if (question.min === '' || question.max === '' || parseInt(question.min, 10) >= parseInt(question.max, 10)) {
          const minmaxid = question.max === '' ? '-max' : '-min';
          return {
            focus: `question-${question.id}${minmaxid}`,
            message: 'Please enter valid slider min and max values',
          };
        }
      }
    }

    return null;
  };

  const saveSurvey = () => {
    const questionsToSend = [];
    let dataToSend = {};
    questions.forEach((question, position) => {
      const mappedQuestion = mapQuestionToAPI(question, position + 1, surveyType === 'quiz');

      questionsToSend.push(mappedQuestion);
    });
    dataToSend = {
      action: readOnlySurvey ? 'read_only' : 'save',
      changeIds: [],
      survey: {
        title: surveyTitle,
        pages: [
          {
            questions: questionsToSend,
          },
        ],
      },
      customConfigs: questionConfig.customConfigs,
    };
    sendMessage(`${JSON.stringify(dataToSend)}`, parentDomain);
  };

  const handleSurveyTypeChange = e => {
    setSurveyType(e.target.attributes.value.value);

    if (sendPayloadOnAllUpdates) {
      saveSurvey();
    }
  };

  const errorClass = 'sm-new-survey__error-outline';

  const updateQuestion = _question => {
    const tmpQuestion = _question;
    tmpQuestion.isUpdated = true;
    const questionsTemp = questions;
    const foundIndex = questionsTemp.findIndex(q => q.id === tmpQuestion.id);
    if (foundIndex > -1) {
      questionsTemp[foundIndex] = tmpQuestion;
      setQuestions([...questionsTemp]);
    }

    if (sendPayloadOnAllUpdates) {
      saveSurvey();
    }

    const errOutline = document.querySelector('.sm-new-survey__input.sm-new-survey__error-outline');
    if (errOutline) {
      errOutline.classList.remove(errorClass);
    }
  };

  const handleQuestionTypeChange = (_question, e) => {
    const tmpQuestion = _question;
    const { value } = e.target.attributes.value;
    tmpQuestion.questionType = value;
    updateQuestion(tmpQuestion);
  };

  const handleQuestionChange = (_question, event) => {
    const tmpQuestion = _question;
    tmpQuestion.question = event.target.value;
    if (surveyType === 'poll') {
      setSurveyTitle(tmpQuestion.question);
    }
    updateQuestion(tmpQuestion);
  };

  const handleMinChange = (_question, e) => {
    const tmpQuestion = _question;
    const val = e.target.value.replace(/[^0-9]+/g, '');
    tmpQuestion.min = val;
    updateQuestion(tmpQuestion);
  };

  const handleMaxChange = (_question, e) => {
    const tmpQuestion = _question;
    const val = e.target.value.replace(/[^0-9]+/g, '');
    tmpQuestion.max = val;
    updateQuestion(tmpQuestion);
  };

  const handleRequiredChange = (_question, e) => {
    const tmpQuestion = _question;
    tmpQuestion.required = e.target.checked;
    updateQuestion(tmpQuestion);
  };

  const addQuestion = () => {
    const questionsTemp = questions;
    const newQuestion = _.cloneDeep(NEW_QUESTION);
    newQuestion.id = new Date().getUTCMilliseconds();
    newQuestion.id = newQuestion.id.toString();
    questionsTemp.push(newQuestion);
    setQuestions([...questionsTemp]);
    const questionTitles = document.getElementsByClassName('sm-new-survey__question-title');
    questionTitles[questionTitles.length - 1].focus();
  };

  const deleteQuestion = question => {
    if (!question.isNew && hasresponses) {
      setDeletePromptQuestion(question);
    } else {
      const questionsToDeleteList = questionsToDelete;
      const filteredQuestions = questions.filter(q => q.id !== question.id);
      if (!question.isNew) {
        questionsToDeleteList.push(question);
      }
      setQuestions([...filteredQuestions]);
      setQuestionsToDelete([...questionsToDeleteList]);
    }
  };

  const handleSurveyTitleChange = e => {
    setSurveyTitle(e.target.value);

    if (sendPayloadOnAllUpdates) {
      saveSurvey();
    }

    const errOutline = document.querySelector('.sm-new-survey__input.sm-new-survey__error-outline');
    if (errOutline) {
      errOutline.classList.remove(errorClass);
    }
  };

  const moveQuestion = (question, down) => {
    const questionsList = questions;
    const from = questionsList.findIndex(q => q.id === question.id);
    const to = down ? from + 1 : from - 1;
    questionsList.splice(from, 1);
    questionsList.splice(to, 0, question);
    setQuestions([...questionsList]);
  };

  const showErrors = errObj => {
    if (errObj && errObj.focus) {
      const input = document.getElementById(errObj.focus);
      input.focus();
      input.classList.add(errorClass);
    }
    setShowToast(true);
    setErrorText(errObj ? errObj.message : '');
  };

  const handleUpdate = () => {
    const questionsToSend = [];
    let dataToSend = {};
    const errors = hasErrors();
    if (errors) {
      showErrors(errors);
      return;
    }
    setShowToast(false);

    const questionCalls = [];
    if (surveyTitle !== surveyDetails.title) {
      questionCalls.push(surveyDetails.id.toString());
    }
    questions.forEach((question, position) => {
      const mappedQuestion = mapQuestionToAPI(question, position + 1, surveyType === 'quiz');
      let actionType;
      let changedData;
      questionsToSend.push(mappedQuestion);

      if ((question.isUpdated || question.initialPosition !== mappedQuestion.position) && !question.isNew) {
        delete mappedQuestion.id;
        delete mappedQuestion.family;
        delete mappedQuestion.subtype;

        questionCalls.push(question.id);
      }
      if (question.isNew) {
        questionCalls.push(question.id);
      }
      questionsToDelete.forEach(questionToDelete => {
        questionCalls.push(questionToDelete.id);
      });

      if (readOnlySurvey) {
        actionType = 'read_only';
        changedData = [];
      } else if (edit) {
        actionType = 'update';
        changedData = questionCalls;
      } else {
        actionType = 'save';
        changedData = questionCalls;
      }

      if (questionCalls.length || readOnlySurvey) {
        dataToSend = {
          action: actionType,
          changeIds: changedData,
          survey: {
            id: surveyDetails.id,
            title: surveyTitle,
            pages: [
              {
                questions: questionsToSend,
              },
            ],
          },
          customConfigs: questionConfig.customConfigs,
        };
        sendMessage(`${JSON.stringify(dataToSend)}`, parentDomain);
      }
    });
  };

  const handleSave = () => {
    const errors = hasErrors();
    if (errors) {
      showErrors(errors);
      return;
    }
    setShowToast(false);
    saveSurvey();
  };

  const handleSubmit = () => {
    if (questions.length === 0) {
      return;
    }

    if (edit && surveyDetails.id) {
      handleUpdate();
    } else {
      handleSave();
    }
  };

  const handleCancel = actionType => {
    if (redirectTo === 'templates') {
      setRedirectUrl('/createwizard/templates');
      setShouldRedirectBackToHome(true);
    } else if (redirectToEditorPage || readOnlySurvey) {
      const dataToSend = {
        action: actionType,
      };
      sendMessage(`${JSON.stringify(dataToSend)}`, parentDomain);
      setCreateWizardConfig({
        ...createWizardConfig,
        redirectTo: '',
        redirectToEditorPage: false,
        readOnlySurvey: false,
        survey: {},
      });
      setRedirectUrl('/createwizard');
      setShouldRedirectBackToHome(true);
    } else {
      setRedirectUrl('/createwizard');
      setShouldRedirectBackToHome(true);
    }
  };

  const onCloseToast = () => setShowToast(false);

  return (
    <Container data-testid="NewSurvey" className="">
      <Grid className="sm-new-survey" noBleeds>
        <Row className="sm-new-survey__top" mt={2}>
          <Col>
            <If condition={shouldRedirectBackToHome}>
              <Redirect to={redirectUrl} />
            </If>
            <Container mt={2} ml={4}>
              <Button
                className="sm-new-survey__back-button"
                size="sm"
                variant="text"
                onClick={() => handleCancel('back')}
              >
                <IconArrowLeft />
                Back
              </Button>
            </Container>
            <Container pl={4}>
              <Typography variant="cardTitle">
                Create&nbsp;
                {surveyTypeSelected}
              </Typography>
            </Container>
          </Col>
        </Row>
        <Row className="sm-new-survey__middle">
          <Col>
            <If condition={inputData.showBadge}>
              <Container className="sm-new-survey__survey-type" my={5}>
                {types && types.length >= 1 && (
                  <If condition={MAX_QUESTIONS === 1}>
                    <Badge size="lg">Poll</Badge>
                    <Else />
                    <Badge size="lg">
                      <Select
                        stretched
                        size="sm"
                        className="sm-new-survey__survey-type sm-new-survey__survey-type-selection"
                        value={surveyType}
                        onChange={handleSurveyTypeChange}
                        disabled={edit || readOnlySurvey}
                      >
                        {types.map(supportedSurveyType => {
                          return (
                            <Option key={uuid()} value={supportedSurveyType.toLowerCase()}>
                              {supportedSurveyType}
                            </Option>
                          );
                        })}
                      </Select>
                    </Badge>
                  </If>
                )}
              </Container>
            </If>
            {MAX_QUESTIONS !== 1 && (
              <Container mb={5}>
                <Input
                  id="surveyTitle"
                  className="surveyTitle"
                  stretched
                  size="sm"
                  label="Title"
                  value={surveyTitle || ''}
                  placeholder="Enter survey title"
                  onChange={handleSurveyTitleChange}
                  disabled={readOnlySurvey}
                />
              </Container>
            )}
            {questions &&
              questions.map((question, index) => {
                const questionID = `question-${question.id}`;
                return (
                  <Container
                    key={`question_container_${question.id}_${index + 1}`}
                    mb={questions && questions.length - 1 === index ? 9 : 0}
                  >
                    {MAX_QUESTIONS !== 1 && !readOnlySurvey && (
                      <Container className="sm-new-survey__question-title" mb={3}>
                        <hr />
                        <Typography component="div" variant="cardTitle">
                          <span className="sm-new-survey__title">Q {index + 1}</span>
                          <Menu
                            show={showMore}
                            className="questionMenu"
                            placement="bottom-right"
                            noRules
                            dense
                            menuTrigger={
                              <Button className="sm-new-survey__more-menu" size="sm" variant="text">
                                <IconMore />
                              </Button>
                            }
                          >
                            <MenuItem onClick={() => deleteQuestion(question)}>Delete question</MenuItem>
                            {index !== 0 && (
                              <MenuItem onClick={() => moveQuestion(question, false)}>Move question up</MenuItem>
                            )}
                            {questions && questions.length - 1 !== index && (
                              <MenuItem onClick={() => moveQuestion(question, true)}>Move question down</MenuItem>
                            )}
                          </Menu>
                        </Typography>
                      </Container>
                    )}
                    <Container mb={5}>
                      <Select
                        stretched
                        size="sm"
                        className="sm-new-survey__question-type"
                        value={question.questionType}
                        onChange={event => handleQuestionTypeChange(question, event)}
                        disabled={(edit && !question.isNew) || readOnlySurvey}
                      >
                        {getQuestionTypes().map(questionType => {
                          return (
                            <Option key={questionType.id} value={questionType.value}>
                              {questionType.text}
                            </Option>
                          );
                        })}
                      </Select>
                    </Container>
                    <Container mb={5}>
                      <Input
                        id={questionID}
                        className="sm-new-survey__question-title sm-new-survey__input"
                        stretched
                        size="sm"
                        label="Question"
                        value={question.question}
                        placeholder="Enter question"
                        onChange={event => handleQuestionChange(question, event)}
                        disabled={readOnlySurvey}
                      />
                    </Container>
                    {MC_QUESTIONS.includes(question.questionType) && (
                      <MultipleChoice
                        key={question.id}
                        question={question}
                        surveyType={surveyType}
                        updateQuestion={updateQuestion}
                        maxAnsChoices={maxAnsChoices}
                        readOnlySurvey={readOnlySurvey}
                      />
                    )}
                    {question.questionType === 'single_textbox' && (
                      <Container>
                        <div className="sm-new-survey__open-ended-mock" />
                      </Container>
                    )}
                    {question.questionType === 'comment_box' && (
                      <Container>
                        <div className="sm-new-survey__open-ended-mock sm-new-survey__open-ended-mock__multiline" />
                      </Container>
                    )}
                    {question.questionType === 'star_rating' && (
                      <Container>
                        <div className="sm-new-survey__star-rating-mock">
                          <span className="sm-new-survey__star-rating-mock-icon">
                            <IconStarFilled />
                          </span>
                          <span className="sm-new-survey__star-rating-mock-icon">
                            <IconStarFilled />
                          </span>
                          <span className="sm-new-survey__star-rating-mock-icon">
                            <IconStarFilled />
                          </span>
                          <span className="sm-new-survey__star-rating-mock-icon">
                            <IconStarFilled />
                          </span>
                          <span className="sm-new-survey__star-rating-mock-icon">
                            <IconStarFilled />
                          </span>
                        </div>
                      </Container>
                    )}
                    {question.questionType === 'slider' && (
                      <Container mb={5}>
                        <Grid noBleeds>
                          <Row>
                            <Col>
                              <Input
                                id={`question-${question.id}-min`}
                                label="Min"
                                size="sm"
                                stretched
                                onChange={event => {
                                  handleMinChange(question, event);
                                }}
                                value={question.min}
                                disabled={readOnlySurvey}
                              />
                            </Col>
                            <Col>
                              <Input
                                id={`question-${question.id}-max`}
                                label="Max"
                                size="sm"
                                stretched
                                onChange={event => {
                                  handleMaxChange(question, event);
                                }}
                                value={question.max}
                                disabled={readOnlySurvey}
                              />
                            </Col>
                          </Row>
                        </Grid>
                        <Container mt={5}>
                          <div className="sm-new-survey__slider-mock" />
                        </Container>
                      </Container>
                    )}
                    {MAX_QUESTIONS !== 1 && (
                      <Container my={5}>
                        <Switch checked={question.required} onChange={event => handleRequiredChange(question, event)} />
                        <span className="sm-new-survey__switch-label">Required</span>
                      </Container>
                    )}
                  </Container>
                );
              })}
          </Col>
        </Row>
        <Row className="sm-new-survey__bottom">
          <Col>
            {MAX_QUESTIONS !== 1 && questions.length < MAX_QUESTIONS && !readOnlySurvey && (
              <Container flex flexJustify="start" my={6}>
                <Button className="sm-new-survey__add-question" variant="ghost" onClick={() => addQuestion()}>
                  + Add question
                </Button>
              </Container>
            )}
          </Col>
          <Col>
            <Toast onClose={onCloseToast} dismissible show={showToast}>
              <ToastTitle>
                Error creating {surveyType}. {errorText}
              </ToastTitle>
            </Toast>

            <Container flex flexJustify="end" my={6}>
              {!hideCancelSurveyButton && (
                <Button className="wds-m-r-2 sm-new-survey__cancel-button" onClick={() => handleCancel('cancel')}>
                  Cancel
                </Button>
              )}
              {!hideCreateSurveyButton && (
                <Button
                  className="sm-new-survey__create-button"
                  onClick={() => handleSubmit()}
                  disabled={
                    (questions && questions.length === 0) ||
                    (MAX_QUESTIONS && questions && questions.length > MAX_QUESTIONS)
                  }
                >
                  {primaryCtaCopy}
                  {showPrimaryCtaIcon ? <IconArrowRight /> : ''}
                </Button>
              )}
            </Container>
          </Col>
        </Row>
      </Grid>
    </Container>
  );
};

NewSurvey.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      surveyTypeSelected: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default NewSurvey;
